import { useState } from 'react';
import type { AdjuntoBreve } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * LA FACTURA DEL GASTO.
 *
 * Cada gasto se carga con su comprobante: el ticket de la gasolinera, la
 * factura del electricista. Pero el comprobante quedaba guardado y no se veía
 * desde ninguna parte, así que la única forma de comprobar un importe era
 * creérselo. Ahora se pulsa la fila y se abre lo que se adjuntó.
 *
 * Y SI NO HAY NINGUNA, SE SUBE AQUÍ. Decir «este gasto no tiene factura» y no
 * ofrecer ponerla convierte el hueco en un reproche: quien lo ve es
 * exactamente quien puede arreglarlo, y en ese momento. Entra imagen o PDF,
 * que es en lo que llegan las facturas.
 *
 * Un gasto casi siempre trae un fichero; a veces dos (el ticket y la factura
 * del mismo pago). Se enseñan todos, que es más honesto que elegir uno.
 */

interface Props {
  idioma: Idioma;
  gastoId: string;
  concepto: string;
  adjuntos: AdjuntoBreve[];
  /** Para que la lista de detrás se entere sin volver a pedirla entera. */
  onSubida: (adjunto: AdjuntoBreve) => void;
  onCerrar: () => void;
}

/** Lo que acepta el almacén y tiene sentido como comprobante de un gasto. */
const ADMITIDOS = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';

/**
 * Una imagen que no se puede pintar no debe dejar un cuadro roto: los HEIC del
 * iPhone no los pinta ningún navegador que no sea de Apple, y el fichero sigue
 * ahí para abrirlo aparte. El PDF se enseña dentro, que es como llega la mitad
 * de las facturas y abrirlo en otra pestaña rompe el repaso.
 */
function Hoja({ idioma, adjunto }: { idioma: Idioma; adjunto: AdjuntoBreve }) {
  const [roto, setRoto] = useState(false);
  const url = api.urlAdjunto(adjunto.id);

  if (adjunto.tipoMime === 'application/pdf') {
    return (
      <div className="factura-pdf">
        <object data={url} type="application/pdf" aria-label={adjunto.nombreFichero}>
          <a href={url} target="_blank" rel="noreferrer" className="enlace evidencia-doc">
            <Icono nombre="documento" /> {adjunto.nombreFichero}
          </a>
        </object>
        <a href={url} target="_blank" rel="noreferrer" className="enlace">
          {t(idioma, 'gasto.abrirAparte')}
        </a>
      </div>
    );
  }

  if (!adjunto.tipoMime.startsWith('image/') || roto) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="enlace evidencia-doc">
        <Icono nombre="documento" /> {adjunto.nombreFichero}
        {roto && <span className="suave"> · {t(idioma, 'inc.fotoNoSeVe')}</span>}
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="factura-hoja">
      <img src={url} alt={adjunto.nombreFichero} onError={() => setRoto(true)} />
    </a>
  );
}

export function FacturaDelGasto({
  idioma,
  gastoId,
  concepto,
  adjuntos,
  onSubida,
  onCerrar,
}: Props) {
  /*
    La lista se lleva aquí dentro: lo que se acaba de subir tiene que verse al
    momento, sin cerrar el diálogo ni recargar la tabla de detrás.
  */
  const [hojas, setHojas] = useState<AdjuntoBreve[]>(adjuntos);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function subir(fichero: File | undefined) {
    if (!fichero) return;
    setError(null);
    setSubiendo(true);
    try {
      const { adjunto } = await api.subirAdjunto('GASTO', gastoId, fichero);
      const breve: AdjuntoBreve = {
        id: adjunto.id,
        nombreFichero: adjunto.nombreFichero,
        tipoMime: adjunto.tipoMime,
      };
      setHojas((antes) => [...antes, breve]);
      onSubida(breve);
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'gasto.noSeSubio'));
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <Modal idioma={idioma} titulo={concepto} icono="billete" onCerrar={onCerrar}>
      {hojas.length === 0 ? (
        <p className="suave">{t(idioma, 'gasto.sinFactura')}</p>
      ) : (
        <div className="factura-hojas">
          {hojas.map((a) => (
            <Hoja key={a.id} idioma={idioma} adjunto={a} />
          ))}
        </div>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <label className="campo factura-subir">
        <span className="etiqueta-campo">
          {t(idioma, hojas.length === 0 ? 'gasto.adjuntarFactura' : 'gasto.adjuntarOtra')}
        </span>
        <input
          type="file"
          accept={ADMITIDOS}
          disabled={subiendo}
          onChange={(e) => {
            void subir(e.target.files?.[0]);
            // Se limpia para poder reintentar el mismo fichero si algo falló.
            e.target.value = '';
          }}
        />
        <small className="suave">
          {subiendo ? t(idioma, 'gasto.subiendo') : t(idioma, 'gasto.formatosFactura')}
        </small>
      </label>
    </Modal>
  );
}

/** El clip que avisa de que la fila tiene factura, y cuántas. */
export function SenalFactura({ idioma, adjuntos }: { idioma: Idioma; adjuntos: AdjuntoBreve[] }) {
  if (adjuntos.length === 0) {
    return (
      <span className="suave" title={t(idioma, 'gasto.sinFactura')}>
        —
      </span>
    );
  }
  return (
    <span className="factura-senal" title={t(idioma, 'gasto.verFactura')}>
      <Icono nombre="documento" />
      {adjuntos.length > 1 && <small>{adjuntos.length}</small>}
    </span>
  );
}
